import { updateLedgerPayment, useUser, useMerchant } from "@auth";
import { toast } from "@components/toast/Toast";
import {
  Badge,
  Button,
  DrawerBody,
  DrawerFooter,
  GridSection,
  GridSectionBody,
  GridSectionHeader,
  GridSectionItem,
  GridSectionSpan,
  Modal,
  SimpleTable,
} from "@components";
import { formatAmount, formatDateTime, formatPascalCase } from "@utils";
import { useEffect, useState } from "react";
import { PencilSquare } from "react-bootstrap-icons";
import {
  reconcileLedgerPayment,
  getLedgerPayment,
  markLedgerPaymentReconciled,
} from "@api";
import { ledgerPaymentStatusColors } from "../common.jsx";
import LedgerPaymentEdit from "../flows/LedgerPaymentEdit.jsx";

const LedgerPaymentDetails = ({ paymentId, onChanged }) => {
  const [payment, setPayment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmManual, setConfirmManual] = useState(false);
  const { hasPermission } = useMerchant();
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const data = await getLedgerPayment(paymentId);
      setPayment(data);
    }

    fetchData();
  }, [paymentId]);

  const reload = async () => {
    const data = await getLedgerPayment(paymentId);
    setPayment(data);
    onChanged();
  };

  const action = async () => {
    setSubmitting(true);

    await reconcileLedgerPayment(payment.id);

    toast({ description: `Posted cash receipt for ${formatAmount(payment.amount)}` });
    setTimeout(updateStatus, 500);
  };

  const markAsReconciled = async () => {
    setSubmitting(true);

    await markLedgerPaymentReconciled(payment.id);

    toast({ description: `Payment for ${formatAmount(payment.amount)} marked as reconciled` });
    onChanged();
    reload();
    setConfirmManual(false);
  };

  
  const updateStatus = async () => {
    const data = await getLedgerPayment(paymentId);
    setPayment(data);

    if (data.cashReceipt || data.failedOn) {
      setSubmitting(false);

      if (onChanged) {
        onChanged();
      }
    } else {
      setTimeout(updateStatus, 500);
    }
  };

  const dataSet = {
    key: "settled-transactions",
    columns: [
      {
        title: "Type",
        key: "transactionType",
        width: "120px",
        render: (x) => formatPascalCase(x.transactionType),
        type: "string",
      },
      {
        title: "Reference",
        key: "transactionNumber",
        width: "120px",
        render: (x) => x.transactionNumber,
        type: "string",
      },
      {
        title: "Transaction date",
        key: "transactionDate",
        width: "120px",
        render: (x) => formatDateTime(x.transactionDate),
        type: "string",
      },
      {
        title: "Amount",
        key: "amount",
        width: "120px",
        render: (x) => formatAmount(x.amount),
        sortFunc: (a, b) => a.amount - b.amount,
        alignment: "right",
      },
    ],
  };

  return (
    payment && (
      <>
        <DrawerBody className="border-spacing-1">
          <GridSection>
            <GridSectionSpan>
              <h2 className="text-3xl font-medium m-0 mr-2">
                {formatAmount(payment.amount)}
              </h2>
              <Badge color={ledgerPaymentStatusColors[payment.status]}>
                {payment.status}
              </Badge>
            </GridSectionSpan>
            {payment.status === "Part-reconciled" && (
              <GridSectionSpan>
                <div className="rounded-md bg-yellow-50 p-4">
                  <div className="flex">
                    <div className="shrink-0"></div>
                    <div className="ml-3">
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          We have posted cash receipt {payment.cashReceipt} for
                          this payment. However, we were unable to assign it to
                          invoices and credit notes within your ERP. Please
                          review the allocated transactions below and apply them
                          to the cash receipt within your ERP system.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </GridSectionSpan>
            )}
            {payment.status === "Change detected" && (
              <GridSectionSpan>
                <div className="rounded-md bg-yellow-50 p-4">
                  <div className="flex">
                    <div className="shrink-0">
                      {/*  <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400" />*/}
                    </div>
                    <div className="ml-3">
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          We have detected {payment.mismatchedAllocations}{" "}
                          change{payment.mismatchedAllocations > 1 && "s"} since
                          this payment was allocated. Its likely this payment
                          has already been posted. Please reconcile within your
                          ERP system and mark this payment as reconciled below.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </GridSectionSpan>
            )}
            <GridSectionItem>
              <GridSectionHeader>Customer name</GridSectionHeader>
              <GridSectionBody>{payment.customerName}</GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Account Code</GridSectionHeader>
              <GridSectionBody>{payment.accountCode}</GridSectionBody>
            </GridSectionItem>
          </GridSection>

          <GridSection>
            <GridSectionItem>
              <GridSectionHeader>Payment date</GridSectionHeader>
              <GridSectionBody>
                {formatDateTime(payment.paymentDate)}
              </GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Payment reference</GridSectionHeader>
              <GridSectionBody>{payment.reference}</GridSectionBody>
            </GridSectionItem>
          </GridSection>

          {payment.nominalAccountCode && (
            <GridSection>
              <GridSectionItem>
                <GridSectionHeader>Destination account code</GridSectionHeader>
                <GridSectionBody>{payment.nominalAccountCode}</GridSectionBody>
              </GridSectionItem>
              <GridSectionItem>
                <GridSectionHeader>Destination account name</GridSectionHeader>
                <GridSectionBody>{payment.nominalAccountName}</GridSectionBody>
              </GridSectionItem>
            </GridSection>
          )}
          {payment.status !== "Allocated" &&
            payment.status !== "Change detected" &&
            payment.status !== "Over-allocated" && (
              <GridSection>
                {(() => {
                  const statusMap = {
                    Error: {
                      byLabel: "Reconciled by",
                      onLabel: "Failed on",
                      timestamp: payment.reconciledOn,
                    },
                    "Reconciled manually": {
                      byLabel: "Reconciled manually by",
                      onLabel: "Reconciled manually on",
                      timestamp: payment.reconciledOn,
                    },
                    Reconciled: {
                      byLabel: "Reconciled by",
                      onLabel: "Reconciled on",
                      timestamp: payment.reconciledOn,
                    },
                    "Part-reconciled": {
                      byLabel: "Reconciled by",
                      onLabel: "Reconciled on",
                      timestamp: payment.reconciledOn,
                    },
                    Reconciling: {
                      byLabel: "Reconciled by",
                      onLabel: "Reconciled on",
                      timestamp: "",
                    },
                  };

                  const { byLabel, onLabel, timestamp } =
                    statusMap[payment.status || "Actioned"];

                  return (
                    <>
                      <GridSectionItem>
                        <GridSectionHeader>{byLabel}</GridSectionHeader>
                        <GridSectionBody>
                          {payment.reconciledBy}
                        </GridSectionBody>
                      </GridSectionItem>
                      <GridSectionItem>
                        <GridSectionHeader>{onLabel}</GridSectionHeader>
                        <GridSectionBody>
                          {formatDateTime(timestamp)}
                        </GridSectionBody>
                      </GridSectionItem>
                      {payment.cashReceipt && (
                        <GridSectionItem>
                          <GridSectionHeader>
                            Cash receipt number
                          </GridSectionHeader>
                          <GridSectionBody>
                            {payment.cashReceipt}
                          </GridSectionBody>
                        </GridSectionItem>
                      )}
                      {payment.failureReason && (
                        <GridSectionItem>
                          <GridSectionHeader>Failure reason</GridSectionHeader>
                          <GridSectionBody>
                            {payment.failureReason}
                          </GridSectionBody>
                        </GridSectionItem>
                      )}
                    </>
                  );
                })()}
              </GridSection>
            )}
          <GridSection>
            <GridSectionSpan>
              <p className="font-semibold m-0">
                {payment.allocationMethod === "CustomerSelected"
                  ? "Settled transactions"
                  : "Allocated transactions"}
              </p>
              {payment.allocationMethod !== "CustomerSelected" &&
                (payment.status === "Allocated" ||
                  payment.status === "Over-allocated") && (
                  <Button
                    className="ml-auto"
                    size="xs"
                    color="text"
                    onClick={() => setShowEditModal(true)}
                  >
                    <PencilSquare className="mr-1" />
                    Edit
                  </Button>
                )}
            </GridSectionSpan>
            {payment.allocationMethod === "CustomerSelected" && (
              <GridSectionSpan>
                <p className="text-sm text-zinc-500">
                  These invoices were specifically chosen by the customer and
                  cannot be edited
                </p>
              </GridSectionSpan>
            )}
            {payment.status === "Over-allocated" && (
              <GridSectionSpan>
                <p className="text-red-500 text-sm">
                  This payment has been assigned to invoices exceeding the
                  payment amount. To post this cash receipt, unallocate invoices
                  by editing the allocated transactions.
                </p>
              </GridSectionSpan>
            )}
            <GridSectionItem>
              <GridSectionHeader>Allocated value</GridSectionHeader>
              <GridSectionBody>
                {formatAmount(payment.amount - payment.unallocated)}
              </GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Unallocated value</GridSectionHeader>
              <GridSectionBody>
                {formatAmount(payment.unallocated)}
              </GridSectionBody>
            </GridSectionItem>

            <GridSectionSpan>
              <SimpleTable config={dataSet} data={payment.allocations} noPad enableExport={true} exportPosition="top" />
            </GridSectionSpan>
          </GridSection>
        </DrawerBody>
        {(payment.status === "Allocated" ||
          payment.status === "Change detected") &&
          hasPermission(updateLedgerPayment) && (
            <DrawerFooter className="position-absolute bottom-0 w-full pe-3 bg-slate-500">
              <div className="flow-root w-full">
                <Button
                  disabled={payment.status === "Change detected"}
                  color="primary"
                  onClick={action}
                  className="float-right ml-3"
                  submitting={submitting}
                >
                  Post cash receipt
                </Button>
                <Button
                  color="secondary"
                  onClick={() => setConfirmManual(true)}
                  className="float-right"
                >
                  Mark as reconciled
                </Button>
              </div>
            </DrawerFooter>
          )}

        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Select allocated invoices"
        >
          <LedgerPaymentEdit payment={payment} onUpdate={reload} />
          <div className="p-4 flex gap-2 justify-end border-t">
            <Button onClick={() => setShowEditModal(false)} color="primary">
              Done
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={confirmManual}
          onClose={() => setConfirmManual(false)}
          title="Manual action"
        >
          <div className="m-5">
            <p>
              By continuing you confirm that this payment has been reconciled
              into your ERP system.
            </p>
            <p>
              This action cannot be undone, are you sure you wish to proceed?
            </p>
          </div>
          <Button
            className="m-5"
            color="danger"
            onClick={markAsReconciled}
            submitting={submitting}
          >
            Confirm
          </Button>
        </Modal>
      </>
    )
  );
};

export default LedgerPaymentDetails;
